/**
 * お知らせバナーの「閉じる」状態の保持 (steering 488)
 *
 * V1 は **localStorage のみ**で、追加の DB write / API を発生させない。
 *
 * **key は `announcementId` 単体ではなく `announcementId + version`** (Mgr Gate 1 裁定の補正)。
 * ID だけで記録すると、利用者が一度閉じたお知らせを運営が重要な内容へ編集しても
 * 同じ ID のまま永久に非表示になってしまう。version 成分には既存の `updatedAt` を使い、
 * このために新しい DB field は増やさない。
 *
 * 保存するのは dismissal key だけで、**本文・PII・組織 ID は保存しない**。
 * 無制限に増えないよう上限を設け、古いものから捨てる。
 */

const STORAGE_KEY = 'acecollect.announcements.dismissed';

/** 保持する dismissal key の上限 (超過分は古いものから捨てる)。 */
export const MAX_DISMISSED_KEYS = 50;

/**
 * dismissal key を組み立てる。
 *
 * `updatedAt` が変われば別 key になる = 編集された お知らせは再表示される。
 */
export function dismissalKey(announcementId: string, updatedAt: string): string {
  return `${announcementId}@${updatedAt}`;
}

function readRaw(): string[] {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    // 文字列以外が混ざっていても落とさず捨てる
    return parsed.filter((v): v is string => typeof v === 'string');
  } catch {
    // localStorage 無効 / JSON 破損。お知らせ表示のために画面を壊さない
    return [];
  }
}

/** 保存済みの dismissal key 一覧。 */
export function readDismissedKeys(): string[] {
  return readRaw();
}

/** そのお知らせ (この版) が閉じられているか。 */
export function isDismissed(
  announcementId: string,
  updatedAt: string,
  dismissed: string[] = readRaw(),
): boolean {
  return dismissed.includes(dismissalKey(announcementId, updatedAt));
}

/**
 * 閉じた状態を記録する。
 *
 * 返り値は保存後の key 一覧 (呼び出し側の state 更新に使う)。
 * localStorage が使えない環境でも例外を投げない。
 */
export function markDismissed(announcementId: string, updatedAt: string): string[] {
  const key = dismissalKey(announcementId, updatedAt);
  const current = readRaw().filter((k) => k !== key);
  current.push(key);

  // 上限超過分は古い (先頭) から捨てる
  const trimmed =
    current.length > MAX_DISMISSED_KEYS
      ? current.slice(current.length - MAX_DISMISSED_KEYS)
      : current;

  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(trimmed));
  } catch {
    // 保存できなくても表示は続行する (次回リロードで再表示されるだけ)
  }
  return trimmed;
}
